/* ─────────────────────────────────────────────────────────────────────────────
   Secciones del apartado "Tickets": un formato de impresión por sección.

   Todas siguen la misma estructura para que se lean igual: primero los textos
   del formato, después los interruptores de qué se incluye, y al final lo
   opcional. La vista previa la dibuja el shell a la derecha.
   ───────────────────────────────────────────────────────────────────────── */
import React, { useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { normalizarImagenTicket, avisoResolucion } from './imagenes';
import {
  T, Seccion, Tarjeta, Campo, Entrada, Selector, FilaToggle, ListaToggles,
  BotonPrimario, BotonSecundario, BotonPeligro, BotonIcono, Aviso, Deslizador,
} from './ui';
import type { PropsConfig } from './SeccionesConfig';

/* ═══ Ticket de venta ════════════════════════════════════════════════════ */
type LineaExtra = { id: string; nombre: string; texto: string };

export function SeccionTicketVenta({ config, actualizar }: PropsConfig) {
  const imagenRef = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState('');

  const lineas: LineaExtra[] = (() => {
    try { return JSON.parse(config.ticket_lineas_extra || '[]'); } catch { return []; }
  })();
  const guardarLineas = (nuevas: LineaExtra[]) => actualizar('ticket_lineas_extra', JSON.stringify(nuevas));
  const editarLinea = (id: string, campo: 'nombre' | 'texto', val: string) =>
    guardarLineas(lineas.map(l => l.id === id ? { ...l, [campo]: val } : l));

  const subirImagen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { dataUrl, ancho } = await normalizarImagenTicket(file);
    actualizar('ticket_imagen_extra_base64', dataUrl);
    setAviso(avisoResolucion(ancho));
  };

  return (
    <Seccion>
      <Tarjeta titulo="Textos" descripcion="El encabezado sale de los datos del negocio, en Tickets › Identidad.">
        <Campo label="Mensaje al pie">
          <Entrada value={config.mensaje_pie || ''} placeholder="¡Gracias por su visita!"
            onChange={e => actualizar('mensaje_pie', e.target.value)} />
        </Campo>
        <Campo label="Tamaño de letra"
          hint="En la impresora térmica solo aplica a las líneas personalizadas y al mensaje final: la tabla de productos mantiene su tamaño para no desalinear columnas.">
          <Selector value={config.ticket_tamano_fuente || 'normal'} style={{ width: 180 }}
            onChange={e => actualizar('ticket_tamano_fuente', e.target.value)}>
            <option value="pequena">Pequeña</option>
            <option value="normal">Normal</option>
            <option value="grande">Grande</option>
          </Selector>
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Impresión">
        <ListaToggles>
          <FilaToggle label="Imprimir ticket al cobrar"
            desc="Si está apagado no sale el ticket al cobrar; el cajón se sigue abriendo. Se puede reimprimir desde el historial"
            cfgKey="ticket_venta_imprimir" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>

      <Tarjeta titulo="Qué se muestra">
        <ListaToggles>
          <FilaToggle label="IVA desglosado" cfgKey="ticket_mostrar_iva" config={config} actualizar={actualizar} />
          <FilaToggle label="Cambio" desc="Solo aplica en pagos en efectivo" cfgKey="ticket_mostrar_cambio" config={config} actualizar={actualizar} />
          <FilaToggle label="Propina" cfgKey="ticket_mostrar_propina" config={config} actualizar={actualizar} />
          <FilaToggle label="Agregados" desc="Extras de cada producto (extra queso, etc.). Su costo ya va sumado en el importe"
            cfgKey="ticket_mostrar_agregados" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>

      <Tarjeta titulo="Líneas personalizadas"
        descripcion="Información extra al final del ticket: WiFi, horario, redes sociales, aviso legal. El nombre es opcional.">
        {lineas.map(l => (
          <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Entrada value={l.nombre} placeholder="Nombre (opcional)" style={{ width: 150, flexShrink: 0 }}
              onChange={e => editarLinea(l.id, 'nombre', e.target.value)} />
            <Entrada value={l.texto} placeholder="Texto de la línea"
              onChange={e => editarLinea(l.id, 'texto', e.target.value)} />
            <BotonIcono onClick={() => guardarLineas(lineas.filter(x => x.id !== l.id))} title="Eliminar línea">
              <X size={16} />
            </BotonIcono>
          </div>
        ))}
        <BotonPrimario style={{ alignSelf: 'flex-start' }}
          onClick={() => guardarLineas([...lineas, { id: `${Date.now()}`, nombre: '', texto: '' }])}>
          <Plus size={15} /> Agregar línea
        </BotonPrimario>
      </Tarjeta>

      <Tarjeta titulo="Imagen al pie"
        descripcion="Se imprime debajo de las líneas personalizadas y antes del mensaje final. Útil para un QR, redes sociales o una promoción.">
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {config.ticket_imagen_extra_base64 && (
            <img src={config.ticket_imagen_extra_base64} alt="imagen al pie"
              style={{
                width: `${config.ticket_imagen_extra_size || 70}px`, flexShrink: 0,
                borderRadius: T.radioChico, border: `1px solid ${T.borde}`, padding: 4,
              }} />
          )}
          <div style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <BotonSecundario onClick={() => imagenRef.current?.click()}>Subir imagen</BotonSecundario>
              {config.ticket_imagen_extra_base64 && (
                <BotonPeligro onClick={() => { actualizar('ticket_imagen_extra_base64', ''); setAviso(''); }}>
                  Quitar imagen
                </BotonPeligro>
              )}
            </div>
            <input ref={imagenRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={subirImagen} />
            {aviso && <Aviso tono="alerta">{aviso}</Aviso>}
            {config.ticket_imagen_extra_base64 && (
              <Deslizador label="Tamaño" sufijo="%" min={20} max={100}
                valor={parseInt(String(config.ticket_imagen_extra_size)) || 70}
                onChange={v => actualizar('ticket_imagen_extra_size', v)} />
            )}
          </div>
        </div>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ Pre-cuenta ═════════════════════════════════════════════════════════ */
export function SeccionPrecuenta({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Textos" descripcion="Se imprime cuando el mesero pide la cuenta, antes de cobrar.">
        <Campo label="Título del ticket">
          <Entrada value={config.precuenta_titulo || ''} placeholder="PRE-CUENTA"
            onChange={e => actualizar('precuenta_titulo', e.target.value)} />
        </Campo>
        <Campo label="Mensaje al pie">
          <Entrada value={config.precuenta_mensaje || ''} placeholder="ESTE NO ES UN COMPROBANTE FISCAL"
            onChange={e => actualizar('precuenta_mensaje', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Qué se muestra">
        <ListaToggles>
          <FilaToggle label="Precios" desc="Si está apagado solo se listan los productos, sin importes"
            cfgKey="precuenta_mostrar_precios" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ Comanda de cocina ══════════════════════════════════════════════════ */
export function SeccionCocina({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Textos" descripcion="Se imprime en la impresora de cocina al enviar una comanda.">
        <Campo label="Encabezado">
          <Entrada value={config.cocina_titulo || ''} placeholder="COCINA"
            onChange={e => actualizar('cocina_titulo', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Qué se incluye">
        <ListaToggles>
          <FilaToggle label="Nombre del mesero" cfgKey="cocina_mostrar_mesero" config={config} actualizar={actualizar} />
          <FilaToggle label="Hora de envío" cfgKey="cocina_mostrar_hora" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ Comanda de barra ═══════════════════════════════════════════════════ */
export function SeccionBarra({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Textos" descripcion="Se imprime en la impresora de barra al enviar una comanda.">
        <Campo label="Encabezado">
          <Entrada value={config.barra_titulo || ''} placeholder="BARRA"
            onChange={e => actualizar('barra_titulo', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Qué se incluye">
        <ListaToggles>
          <FilaToggle label="Nombre del mesero" cfgKey="barra_mostrar_mesero" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ Corte de turno ═════════════════════════════════════════════════════ */
export function SeccionCorte({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Textos" descripcion="Se imprime al cerrar el turno de caja.">
        <Campo label="Título del corte">
          <Entrada value={config.corte_titulo || ''} placeholder="CORTE DE TURNO"
            onChange={e => actualizar('corte_titulo', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Secciones a incluir" descripcion="El resumen de caja siempre se imprime; lo demás es opcional.">
        <ListaToggles>
          <FilaToggle label="Formas de pago" desc="Efectivo, tarjeta y transferencia con sus totales"
            cfgKey="corte_mostrar_formas_pago" config={config} actualizar={actualizar} />
          <FilaToggle label="Comisiones de terminal" desc="Cuánto retiene la terminal de lo cobrado con tarjeta y el neto a depositar. Se configura en Configuración › Comisiones de terminal"
            cfgKey="corte_mostrar_comisiones" config={config} actualizar={actualizar} />
          <FilaToggle label="Propinas por forma de pago" desc="Desglose de propinas cobradas por método"
            cfgKey="corte_mostrar_propinas" config={config} actualizar={actualizar} />
          <FilaToggle label="Venta por tipo de producto" desc="Total vendido por categoría: bebidas, alimentos, etc."
            cfgKey="corte_mostrar_categorias" config={config} actualizar={actualizar} />
          <FilaToggle label="Movimientos de caja" desc="En qué se fue el efectivo: propinas pagadas, proveedores, gastos… Los conceptos se editan en Configuración › Movimientos de caja"
            cfgKey="corte_mostrar_movimientos" config={config} actualizar={actualizar} />
          <FilaToggle label="Resumen de cuentas" desc="Normales, canceladas, descuentos, comensales y promedios"
            cfgKey="corte_mostrar_cuentas" config={config} actualizar={actualizar} />
          <FilaToggle label="Declaración por forma de pago" desc="Sistema contra declarado, con sobrante o faltante"
            cfgKey="corte_mostrar_declaracion" config={config} actualizar={actualizar} />
          <FilaToggle label="Sección de firmas" desc="Nombre, firma del cajero y firma del gerente"
            cfgKey="corte_mostrar_firma" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ Propinas ═══════════════════════════════════════════════════════════ */
export function SeccionPropinas({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Textos" descripcion="Resumen de propinas que se imprime al cierre del turno.">
        <Campo label="Título del reporte">
          <Entrada value={config.propinas_titulo || ''} placeholder="PROPINAS DEL TURNO"
            onChange={e => actualizar('propinas_titulo', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Qué se incluye">
        <ListaToggles>
          <FilaToggle label="Línea de firma del mesero" cfgKey="propinas_mostrar_firma" config={config} actualizar={actualizar} />
        </ListaToggles>
      </Tarjeta>
    </Seccion>
  );
}
